import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, Inbox, Bike, Store, Mail } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAdmin } from "@/admin/AdminLayout";
import { fetchLeads, OWN_EXPORT_URL } from "@/admin/adminApi";
import {
  PageHeader,
  ResourceView,
  StatCard,
  Loading,
  fmtDate,
} from "@/admin/adminComponents";

const truncate = (s, n = 60) => {
  const str = String(s || "");
  return str.length > n ? `${str.slice(0, n)}\u2026` : str;
};

const createdCol = { key: "created_at", label: "Received", render: (r) => <span className="whitespace-nowrap text-xs">{fmtDate(r.created_at)}</span> };

const TABS = [
  {
    key: "waitlist",
    label: "Waitlist",
    icon: Inbox,
    searchKeys: ["email", "source"],
    searchPlaceholder: "Search by email or source...",
    detailTitle: (r) => r.email,
    columns: [
      { key: "email", label: "Email", render: (r) => <span className="font-medium text-halalgo-ink">{r.email}</span> },
      { key: "source", label: "Source" },
      createdCol,
    ],
  },
  {
    key: "riders",
    label: "Rider Applications",
    icon: Bike,
    searchKeys: ["name", "email", "phone", "source"],
    searchPlaceholder: "Search by name, email, phone...",
    detailTitle: (r) => r.name,
    columns: [
      { key: "name", label: "Name", render: (r) => <span className="font-medium text-halalgo-ink">{r.name}</span> },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone", render: (r) => <span className="whitespace-nowrap">{r.phone}</span> },
      { key: "source", label: "Source" },
      createdCol,
    ],
  },
  {
    key: "merchants",
    label: "Restaurant Partners",
    icon: Store,
    searchKeys: ["restaurant_name", "owner_name", "email", "phone"],
    searchPlaceholder: "Search by restaurant, owner, email...",
    detailTitle: (r) => r.restaurant_name,
    columns: [
      { key: "restaurant_name", label: "Restaurant", render: (r) => <span className="font-medium text-halalgo-ink">{r.restaurant_name}</span> },
      { key: "owner_name", label: "Owner" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone", render: (r) => <span className="whitespace-nowrap">{r.phone}</span> },
      createdCol,
    ],
  },
  {
    key: "contacts",
    label: "Contact Messages",
    icon: Mail,
    searchKeys: ["name", "email", "role", "message"],
    searchPlaceholder: "Search by name, email, message...",
    detailTitle: (r) => r.name,
    columns: [
      { key: "name", label: "Name", render: (r) => <span className="font-medium text-halalgo-ink">{r.name}</span> },
      { key: "email", label: "Email" },
      { key: "role", label: "Role", render: (r) => r.role || "-" },
      { key: "message", label: "Message", render: (r) => <span className="text-halalgo-ink/70">{truncate(r.message)}</span> },
      createdCol,
    ],
  },
];

export default function Leads() {
  const { token } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchLeads(token);
      setData(res);
    } catch (e) {
      toast.error("Could not load leads from the server.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    setRefreshing(true);
    load();
  };

  const counts = data?.counts || {};

  return (
    <div data-testid="admin-leads-page">
      <PageHeader
        title="Leads"
        subtitle="Signups captured on the Halal GO marketing website."
        actions={
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="leads-refresh-button" className="border-halalgo-kraft-200">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Inbox} label="Waitlist" value={counts.waitlist ?? 0} />
        <StatCard icon={Bike} label="Rider Applications" value={counts.riders ?? 0} />
        <StatCard icon={Store} label="Restaurant Partners" value={counts.merchants ?? 0} />
        <StatCard icon={Mail} label="Contact Messages" value={counts.contacts ?? 0} />
      </div>

      {loading ? (
        <Loading label="Loading leads..." />
      ) : (
        <Tabs defaultValue="waitlist" className="w-full">
          <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-halalgo-paper2 p-1">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`leads-tab-${t.key}`} className="data-[state=active]:bg-white data-[state=active]:text-halalgo-green-800">
                <t.icon className="mr-1.5 h-4 w-4" /> {t.label}
                <span className="ml-1.5 rounded bg-halalgo-kraft-100 px-1.5 text-[11px] font-semibold text-halalgo-ink/60">{counts[t.key] ?? 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key}>
              <ResourceView
                columns={t.columns}
                rows={data?.[t.key] || []}
                testId={`leads-${t.key}-table`}
                searchKeys={t.searchKeys}
                searchPlaceholder={t.searchPlaceholder}
                detailTitle={t.detailTitle}
                empty={`No ${t.label.toLowerCase()} yet.`}
                right={
                  <Button asChild variant="outline" size="sm" className="border-halalgo-kraft-200">
                    <a href={OWN_EXPORT_URL(t.key, token)} data-testid={`leads-export-${t.key}`}>
                      <Download className="h-4 w-4" /> Export CSV
                    </a>
                  </Button>
                }
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
